import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-skilltrade-app-2026.cloudfunctions.net';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const STATE_NAMES = {
  'AL':'Alabama','AK':'Alaska','AZ':'Arizona','AR':'Arkansas','CA':'California',
  'CO':'Colorado','CT':'Connecticut','DE':'Delaware','FL':'Florida','GA':'Georgia',
  'HI':'Hawaii','ID':'Idaho','IL':'Illinois','IN':'Indiana','IA':'Iowa',
  'KS':'Kansas','KY':'Kentucky','LA':'Louisiana','ME':'Maine','MD':'Maryland',
  'MA':'Massachusetts','MI':'Michigan','MN':'Minnesota','MS':'Mississippi',
  'MO':'Missouri','MT':'Montana','NE':'Nebraska','NV':'Nevada','NH':'New Hampshire',
  'NJ':'New Jersey','NM':'New Mexico','NY':'New York','NC':'North Carolina',
  'ND':'North Dakota','OH':'Ohio','OK':'Oklahoma','OR':'Oregon','PA':'Pennsylvania',
  'RI':'Rhode Island','SC':'South Carolina','SD':'South Dakota','TN':'Tennessee',
  'TX':'Texas','UT':'Utah','VT':'Vermont','VA':'Virginia','WA':'Washington',
  'WV':'West Virginia','WI':'Wisconsin','WY':'Wyoming',
};

const TRADES = [
  'Electrical','Plumbing','Roofing','HVAC','Carpentry',
  'Welding','Painting','General Construction','Masonry',
  'Drywall','Flooring','Landscaping',
];

const CONTRACTOR_AGREEMENTS = [
  { id: 'not_employer', text: 'I understand TradePower is a technology marketplace and not an employer of any worker', required: true },
  { id: 'supervising', text: 'I am solely responsible for supervising and directing any worker I engage through the TradePower marketplace', required: true },
  { id: 'liability', text: 'I assume all liability for work performed on my job sites', required: true },
  { id: 'insurance', text: 'I confirm my insurance covers workers engaged through TradePower', required: false },
  { id: 'no_tradepower_liability', text: 'I understand TradePower assumes no liability for work performed, injuries, or damages on job sites', required: true },
];

// ─── Card Setup Form ───────────────────────────────────────────────────────
function CardSetupForm({ contractorId, contractorEmail, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/createContractorSetup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId, contractorEmail, paymentType: 'card' }),
      });
      const data = await res.json();
      if (!data.clientSecret) throw new Error('Failed to create setup intent');
      const result = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: contractorEmail },
        },
      });
      if (result.error) { setError(result.error.message); setSaving(false); return; }
      const confirmRes = await fetch(`${CLOUD_FUNCTIONS_BASE}/confirmContractorPaymentMethod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId, paymentType: 'card' }),
      });
      const confirmData = await confirmRes.json();
      if (confirmData.setupDone) {
        onSuccess(confirmData);
      } else {
        setError('Card saved but verification failed. Please try again.');
      }
    } catch (err) { setError(err.message || 'Something went wrong.'); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={formStyles.cardWrapper}>
        <CardElement options={{
          style: {
            base: { fontSize: '16px', color: '#1a1a2e', '::placeholder': { color: '#aaa' } },
            invalid: { color: '#dc3545' },
          },
        }} />
      </div>
      {error && <p style={formStyles.error}>⚠️ {error}</p>}
      <div style={formStyles.buttons}>
        <button type="button" style={formStyles.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" style={saving ? formStyles.saveBtnDisabled : formStyles.saveBtn} disabled={saving || !stripe}>
          {saving ? '⏳ Saving...' : '💳 Save Card'}
        </button>
      </div>
    </form>
  );
}

// ─── ACH Setup Form ────────────────────────────────────────────────────────
function ACHSetupForm({ contractorId, contractorEmail, companyName, onSuccess, onCancel }) {
  const stripe = useStripe();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/createContractorSetup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId, contractorEmail, companyName, paymentType: 'ach' }),
      });
      const data = await res.json();
      if (!data.clientSecret) throw new Error('Failed to create setup intent');
      const result = await stripe.collectBankAccountForSetup({
        clientSecret: data.clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: companyName || contractorEmail,
              email: contractorEmail,
            },
          },
        },
      });
      if (result.error) { setError(result.error.message); setSaving(false); return; }
      if (result.setupIntent?.status === 'requires_confirmation') {
        const confirmResult = await stripe.confirmUsBankAccountSetup(data.clientSecret);
        if (confirmResult.error) { setError(confirmResult.error.message); setSaving(false); return; }
      }
      const confirmRes = await fetch(`${CLOUD_FUNCTIONS_BASE}/confirmContractorPaymentMethod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractorId, paymentType: 'ach' }),
      });
      const confirmData = await confirmRes.json();
      if (confirmData.setupDone) {
        onSuccess(confirmData);
      } else {
        setError('Bank account saved but verification failed. Please try again.');
      }
    } catch (err) { setError(err.message || 'Something went wrong.'); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={formStyles.achInfo}>
        <p style={formStyles.achInfoTitle}>🏦 How ACH Bank Payment Works</p>
        <p style={formStyles.achInfoText}>
          You'll be connected to your bank securely via Stripe Financial Connections.
          Log in to your bank and select your business checking account.
          No manual routing/account numbers needed.
        </p>
      </div>
      <div style={formStyles.achWarning}>
        ⏱️ ACH takes 3–5 business days to settle. Workers are still paid on Thursday —
        TradePower fronts the payment while your ACH clears.
      </div>
      {error && <p style={formStyles.error}>⚠️ {error}</p>}
      <div style={formStyles.buttons}>
        <button type="button" style={formStyles.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" style={saving ? formStyles.saveBtnDisabled : formStyles.achSaveBtn} disabled={saving || !stripe}>
          {saving ? '⏳ Connecting to Bank...' : '🏦 Connect Bank Account →'}
        </button>
      </div>
    </form>
  );
}

const formStyles = {
  cardWrapper: { border: '1px solid #ddd', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', backgroundColor: 'white' },
  error: { fontSize: '13px', color: '#dc3545', marginBottom: '12px' },
  buttons: { display: 'flex', gap: '10px' },
  cancelBtn: { flex: 1, padding: '11px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveBtn: { flex: 2, padding: '11px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  saveBtnDisabled: { flex: 2, padding: '11px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
  achSaveBtn: { flex: 2, padding: '11px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  achInfo: { backgroundColor: '#e8f4fd', borderRadius: '8px', padding: '14px', marginBottom: '14px' },
  achInfoTitle: { fontSize: '14px', fontWeight: 'bold', color: '#2c5282', marginBottom: '6px' },
  achInfoText: { fontSize: '13px', color: '#2c5282', lineHeight: '1.5' },
  achWarning: { backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#856404', marginBottom: '14px', lineHeight: '1.5' },
};

// ─── Main Component ────────────────────────────────────────────────────────
function ContractorProfile() {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [activeTab, setActiveTab] = useState('profile');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [trades, setTrades] = useState([]);
  const [agreements, setAgreements] = useState({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stripeSetupDone, setStripeSetupDone] = useState(false);
  const [paymentMethodType, setPaymentMethodType] = useState('card');
  const [cardLast4, setCardLast4] = useState('');
  const [cardBrand, setCardBrand] = useState('');
  const [achBankName, setAchBankName] = useState('');
  const [achLast4, setAchLast4] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);
  const [showACHForm, setShowACHForm] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const [billingRecords, setBillingRecords] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const allRequiredChecked = CONTRACTOR_AGREEMENTS
    .filter(a => a.required)
    .every(a => agreements[a.id]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const docSnap = await getDoc(doc(db, 'contractorProfiles', user.uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCompanyName(data.companyName || '');
          setContactName(data.contactName || '');
          setPhone(data.phone || '');
          setAddress(data.address || '');
          setState(data.state || '');
          setLicenseNumber(data.licenseNumber || '');
          setLicenseState(data.licenseState || '');
          setTrades(data.trades || []);
          setAgreements(data.agreements || {});
          setStripeSetupDone(data.stripeSetupDone || false);
          setPaymentMethodType(data.stripePaymentMethodType || 'card');
          setCardLast4(data.stripeCardLast4 || '');
          setCardBrand(data.stripeCardBrand || '');
          setAchBankName(data.stripeACHBankName || '');
          setAchLast4(data.stripeACHLast4 || '');
          setPaymentFailed(data.paymentFailed || false);
        }
        // Load smsEnabled from users collection
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setSmsEnabled(userData.smsEnabled || false);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'payments') loadBillingRecords();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillingRecords = async () => {
    if (!user) return;
    setBillingLoading(true);
    try {
      const q = query(collection(db, 'billing_records'), where('contractorId', '==', user.uid));
      const snap = await getDocs(q);
      const records = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aDate = a.paidAt?.toDate?.() || new Date(0);
          const bDate = b.paidAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });
      setBillingRecords(records);
    } catch (err) { console.error(err); }
    setBillingLoading(false);
  };

  const handlePaymentSuccess = (data) => {
    setStripeSetupDone(true);
    setPaymentMethodType(data.paymentType || 'card');
    if (data.paymentType === 'ach') {
      setAchBankName(data.bankName || '');
      setAchLast4(data.last4 || '');
    } else {
      setCardLast4(data.last4 || '');
      setCardBrand(data.brand || '');
    }
    setShowCardForm(false);
    setShowACHForm(false);
    setPaymentFailed(false);
  };

  const handleOpenForm = (type) => {
    setShowCardForm(false);
    setShowACHForm(false);
    if (type === 'card') setShowCardForm(true);
    else setShowACHForm(true);
  };

  const handleCancelForm = () => {
    setShowCardForm(false);
    setShowACHForm(false);
  };

  const toggleTrade = (trade) => {
    setTrades(prev => prev.includes(trade) ? prev.filter(t => t !== trade) : [...prev, trade]);
  };

  const toggleAgreement = (id) => {
    setAgreements(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!allRequiredChecked) {
      alert('Please acknowledge all required platform agreements before saving.');
      return;
    }
    if (smsEnabled && !phone.trim()) {
      alert('Please enter a phone number to enable SMS alerts.');
      return;
    }
    setSaving(true);
    try {
      // Save to contractorProfiles
      await setDoc(doc(db, 'contractorProfiles', user.uid), {
        companyName, contactName, phone, address, state,
        licenseNumber, licenseState, trades, agreements,
        insuranceConfirmed: agreements['insurance'] || false,
        email: user.email, userId: user.uid,
        updatedAt: new Date(),
      }, { merge: true });

      // Save phone + smsEnabled to users collection (where Cloud Functions read from)
      await setDoc(doc(db, 'users', user.uid), {
        phone, smsEnabled,
      }, { merge: true });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/deleteUserAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, userType: 'contractor' }),
      });
      const data = await res.json();
      if (data.blocked) {
        setDeleteError(data.message);
        setDeleting(false);
        return;
      }
      if (data.success) {
        await auth.signOut();
        navigate('/login');
      } else {
        setDeleteError('Something went wrong. Please try again or contact support.');
      }
    } catch (err) {
      setDeleteError('Something went wrong. Please try again or contact support.');
    }
    setDeleting(false);
  };

  const formatCardBrand = (brand) => brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';

  const totalBilled = billingRecords
    .filter(r => r.status === 'paid' || r.status === 'processing')
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  if (loading) return <div style={styles.loadingContainer}><p>Loading your profile...</p></div>;

  const activeMethodDisplay = () => {
    if (!stripeSetupDone) return null;
    if (paymentMethodType === 'ach' && achLast4) {
      return { icon: '🏦', label: `${achBankName || 'Bank'} ••••${achLast4}`, type: 'ACH Bank Transfer', color: '#2c5282', bg: '#e8f4fd' };
    }
    if (cardLast4) {
      return { icon: '💳', label: `${formatCardBrand(cardBrand)} ••••${cardLast4}`, type: 'Credit / Debit Card', color: '#155724', bg: '#d4edda' };
    }
    return null;
  };
  const activeMethod = activeMethodDisplay();

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>Contractor Profile</h2>
        <p style={styles.subtitle}>Manage your company account and billing</p>

        {saved && <div style={styles.successBanner}>✅ Profile saved successfully!</div>}

        {paymentFailed && activeTab !== 'payments' && (
          <div style={styles.paymentFailedAlert}>
            <span>⚠️ A recent payment failed. Please update your payment method to avoid job suspension.</span>
            <button style={styles.paymentFailedBtn} onClick={() => setActiveTab('payments')}>Fix Now →</button>
          </div>
        )}

        <div style={styles.tabNav}>
          <button style={activeTab === 'profile' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('profile')}>
            🏗️ Profile & Agreements
          </button>
          <button style={activeTab === 'payments' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('payments')}>
            {stripeSetupDone ? '💳 Payments ✅' : '💳 Payments ⚠️'}
          </button>
        </div>

        {activeTab === 'profile' && (
          <form onSubmit={handleSave} style={styles.form}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>🏗️ Company Information</h3>
              <label style={styles.fieldLabel}>Company Name</label>
              <input style={styles.input} type="text" placeholder="e.g. Smith Electrical LLC" value={companyName} onChange={e => setCompanyName(e.target.value)} required />
              <label style={styles.fieldLabel}>Primary Contact Name</label>
              <input style={styles.input} type="text" placeholder="Your full name" value={contactName} onChange={e => setContactName(e.target.value)} required />
              <label style={styles.fieldLabel}>Phone Number</label>
              <input style={styles.input} type="tel" placeholder="Business phone number" value={phone} onChange={e => setPhone(e.target.value)} required />

              {/* SMS Toggle */}
              <div style={styles.smsToggleBox}>
                <div style={styles.smsToggleLeft}>
                  <p style={styles.smsToggleTitle}>📱 Text Message Alerts</p>
                  <p style={styles.smsToggleDesc}>
                    Receive SMS alerts when workers accept jobs and when messages arrive.
                    {!phone.trim() && ' Enter your phone number above to enable.'}
                  </p>
                </div>
                <button
                  type="button"
                  style={smsEnabled ? styles.smsToggleOn : styles.smsToggleOff}
                  onClick={() => {
                    if (!phone.trim() && !smsEnabled) {
                      alert('Please enter your phone number first.');
                      return;
                    }
                    setSmsEnabled(!smsEnabled);
                  }}>
                  {smsEnabled ? 'ON' : 'OFF'}
                </button>
              </div>

              <label style={styles.fieldLabel}>Business Address</label>
              <input style={styles.input} type="text" placeholder="Street Address, City, State, ZIP" value={address} onChange={e => setAddress(e.target.value)} required />
              <label style={styles.fieldLabel}>State of Operation</label>
              <select style={styles.input} value={state} onChange={e => setState(e.target.value)} required>
                <option value="">Select state</option>
                {US_STATES.map(s => <option key={s} value={s}>{s} — {STATE_NAMES[s]}</option>)}
              </select>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>📋 License Information</h3>
              <p style={styles.hint}>Enter your contractor license details</p>
              <div style={styles.row}>
                <div style={{ flex: 2 }}>
                  <label style={styles.fieldLabel}>Contractor License Number</label>
                  <input style={styles.input} type="text" placeholder="License number" value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.fieldLabel}>License State</label>
                  <select style={styles.input} value={licenseState} onChange={e => setLicenseState(e.target.value)}>
                    <option value="">State</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>🔧 Trades You Hire For</h3>
              <p style={styles.hint}>Select all trades you typically hire workers for</p>
              <div style={styles.tagGrid}>
                {TRADES.map(trade => (
                  <button key={trade} type="button"
                    style={trades.includes(trade) ? styles.tagActive : styles.tag}
                    onClick={() => toggleTrade(trade)}>
                    {trade}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>⚖️ Platform Agreements</h3>
              <p style={styles.hint}>All required items must be confirmed before your account can be used to hire workers.</p>
              <div style={styles.agreementsList}>
                {CONTRACTOR_AGREEMENTS.map(agreement => (
                  <div key={agreement.id}
                    style={agreements[agreement.id] ? styles.agreementItemChecked : styles.agreementItem}
                    onClick={() => toggleAgreement(agreement.id)}>
                    <div style={styles.agreementCheckbox}>
                      {agreements[agreement.id]
                        ? <span style={styles.checkboxChecked}>✓</span>
                        : <span style={styles.checkboxUnchecked} />}
                    </div>
                    <div style={styles.agreementTextBlock}>
                      <p style={styles.agreementText}>{agreement.text}</p>
                      {agreement.required
                        ? <span style={styles.requiredTag}>Required</span>
                        : <span style={styles.optionalTag}>Optional</span>}
                    </div>
                  </div>
                ))}
              </div>
              {!agreements['insurance'] && (
                <div style={styles.insuranceWarning}>
                  ⚠️ We strongly recommend confirming your insurance covers workers engaged through TradePower.
                </div>
              )}
              {!allRequiredChecked && (
                <div style={styles.agreementWarning}>⚠️ Please acknowledge all required agreements before saving.</div>
              )}
              <div style={styles.disclaimer}>
                <p style={styles.disclaimerTitle}>⚠️ Platform Disclaimer</p>
                <p style={styles.disclaimerText}>
                  TradePower Solutions Inc. is a technology marketplace platform only. TradePower does not employ,
                  supervise, or direct any worker, and assumes no liability for work performed, injuries, or damages
                  on job sites. Workers and contractors are solely responsible for their own coverage, compliance, and safety.
                </p>
              </div>
            </div>

            <button style={saving ? styles.saveButtonDisabled : styles.saveButton} type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>

            <div style={styles.deleteSection}>
              <div style={styles.deleteDivider} />
              <h4 style={styles.deleteTitle}>⚠️ Delete Account</h4>
              <p style={styles.deleteText}>
                Permanently delete your TradePower contractor account. This action cannot be undone.
                Financial records are retained for tax and legal compliance.
              </p>
              {!showDeleteConfirm ? (
                <button type="button" style={styles.deleteBtn}
                  onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); setDeleteConfirmText(''); }}>
                  Delete My Account
                </button>
              ) : (
                <div style={styles.deleteConfirmBox}>
                  <p style={styles.deleteConfirmTitle}>⚠️ Are you absolutely sure?</p>
                  <div style={styles.deleteConfirmList}>
                    <p style={styles.deleteConfirmItem}>✗ Your company profile will be permanently deleted</p>
                    <p style={styles.deleteConfirmItem}>✗ You will lose access to all job and billing history</p>
                    <p style={styles.deleteConfirmItem}>✗ This cannot be reversed</p>
                    <p style={{ ...styles.deleteConfirmItem, color: '#28a745' }}>✓ Financial records kept for tax compliance</p>
                  </div>
                  <label style={styles.deleteConfirmLabel}>Type <strong>DELETE</strong> to confirm:</label>
                  <input style={styles.deleteConfirmInput} type="text" placeholder="DELETE"
                    value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
                  {deleteError && <p style={styles.deleteError}>{deleteError}</p>}
                  <div style={styles.deleteConfirmButtons}>
                    <button type="button" style={styles.deleteCancelBtn}
                      onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); setDeleteConfirmText(''); }}
                      disabled={deleting}>Cancel</button>
                    <button type="button"
                      style={deleting || deleteConfirmText !== 'DELETE' ? styles.deleteConfirmBtnDisabled : styles.deleteConfirmBtn}
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirmText !== 'DELETE'}>
                      {deleting ? '⏳ Deleting...' : '🗑 Permanently Delete Account'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}

        {activeTab === 'payments' && (
          <div style={styles.paymentsContent}>
            {paymentFailed && (
              <div style={styles.paymentFailedBox}>
                <p style={styles.paymentFailedTitle}>⚠️ Recent Payment Failed</p>
                <p style={styles.paymentFailedText}>
                  We were unable to charge your payment method. Please update it within 48 hours
                  to avoid suspension of your ability to post jobs.
                </p>
              </div>
            )}

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>💳 Payment Method</h3>
              <p style={styles.hint}>
                Choose how you want to pay for confirmed hours. You can switch between card and
                ACH at any time. Changes take effect on your next billing cycle.
              </p>

              {!showCardForm && !showACHForm && (
                <div style={styles.methodToggleRow}>
                  <div style={paymentMethodType === 'card' && stripeSetupDone ? styles.methodOptionActive : styles.methodOption}>
                    <div style={styles.methodOptionHeader}>
                      <span style={styles.methodOptionIcon}>💳</span>
                      <div style={styles.methodOptionInfo}>
                        <p style={styles.methodOptionTitle}>Credit / Debit Card</p>
                        <p style={styles.methodOptionSub}>2.9% + 30¢ per transaction • Instant settlement</p>
                      </div>
                      {paymentMethodType === 'card' && stripeSetupDone && cardLast4 && (
                        <span style={styles.activeMethodBadge}>✅ Active</span>
                      )}
                    </div>
                    {paymentMethodType === 'card' && stripeSetupDone && cardLast4 && (
                      <div style={styles.methodOnFileRow}>
                        <span style={styles.methodOnFileLabel}>{formatCardBrand(cardBrand)} ••••{cardLast4} on file</span>
                        <button style={styles.updateMethodBtn} onClick={() => handleOpenForm('card')}>🔄 Update Card</button>
                      </div>
                    )}
                    {(!stripeSetupDone || paymentMethodType !== 'card' || !cardLast4) && (
                      <button style={styles.addMethodBtn} onClick={() => handleOpenForm('card')}>
                        {stripeSetupDone && paymentMethodType === 'ach' ? '↩️ Switch to Card' : '➕ Add Card'}
                      </button>
                    )}
                  </div>

                  <div style={paymentMethodType === 'ach' && stripeSetupDone ? styles.methodOptionActive : styles.methodOption}>
                    <div style={styles.methodOptionHeader}>
                      <span style={styles.methodOptionIcon}>🏦</span>
                      <div style={styles.methodOptionInfo}>
                        <p style={styles.methodOptionTitle}>Bank Account (ACH)</p>
                        <p style={styles.methodOptionSub}>0.8% capped at $5 • 3–5 day settlement</p>
                      </div>
                      {paymentMethodType === 'ach' && stripeSetupDone && achLast4 && (
                        <span style={styles.activeMethodBadgeACH}>✅ Active</span>
                      )}
                    </div>
                    {paymentMethodType === 'ach' && stripeSetupDone && achLast4 && (
                      <div style={styles.methodOnFileRow}>
                        <span style={styles.methodOnFileLabel}>{achBankName || 'Bank'} ••••{achLast4} on file</span>
                        <button style={styles.updateMethodBtn} onClick={() => handleOpenForm('ach')}>🔄 Update Bank</button>
                      </div>
                    )}
                    {(!stripeSetupDone || paymentMethodType !== 'ach' || !achLast4) && (
                      <div>
                        <button style={styles.addMethodBtnACH} onClick={() => handleOpenForm('ach')}>
                          {stripeSetupDone && paymentMethodType === 'card' ? '↩️ Switch to ACH Bank' : '➕ Connect Bank Account'}
                        </button>
                        {!stripeSetupDone && (
                          <p style={styles.achRecommendedNote}>⭐ Recommended for weekly invoices over $2,500</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {stripeSetupDone && activeMethod && !showCardForm && !showACHForm && (
                <div style={{ ...styles.activeMethodSummary, backgroundColor: activeMethod.bg }}>
                  <span style={styles.activeMethodSummaryIcon}>{activeMethod.icon}</span>
                  <div>
                    <p style={{ ...styles.activeMethodSummaryLabel, color: activeMethod.color }}>
                      Currently charging via {activeMethod.type}
                    </p>
                    <p style={{ ...styles.activeMethodSummaryValue, color: activeMethod.color }}>
                      {activeMethod.label}
                    </p>
                  </div>
                </div>
              )}

              {showCardForm && (
                <div style={styles.cardFormBox}>
                  <h4 style={styles.cardFormTitle}>💳 {stripeSetupDone ? 'Update Card' : 'Add Credit / Debit Card'}</h4>
                  <p style={styles.cardFormSubtitle}>Charged weekly every Thursday. No charge today.</p>
                  <div style={styles.cardFormInfoBox}>
                    <span>ℹ️</span>
                    <span style={{ fontSize: '13px', color: '#2c5282', lineHeight: '1.5' }}>
                      TradePower never stores your card. All processing is handled by Stripe (PCI Level 1).
                    </span>
                  </div>
                  <Elements stripe={stripePromise}>
                    <CardSetupForm
                      contractorId={user?.uid}
                      contractorEmail={user?.email}
                      onSuccess={handlePaymentSuccess}
                      onCancel={handleCancelForm}
                    />
                  </Elements>
                </div>
              )}

              {showACHForm && (
                <div style={styles.cardFormBox}>
                  <h4 style={styles.cardFormTitle}>🏦 {stripeSetupDone ? 'Update Bank Account' : 'Connect Bank Account (ACH)'}</h4>
                  <Elements stripe={stripePromise}>
                    <ACHSetupForm
                      contractorId={user?.uid}
                      contractorEmail={user?.email}
                      companyName={companyName}
                      onSuccess={handlePaymentSuccess}
                      onCancel={handleCancelForm}
                    />
                  </Elements>
                </div>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>📊 Billing Summary</h3>
              <div style={styles.billingSummaryRow}>
                <div style={styles.billingSummaryCard}>
                  <span style={styles.billingSummaryAmount}>${totalBilled.toLocaleString()}</span>
                  <span style={styles.billingSummaryLabel}>Total Billed</span>
                </div>
                <div style={styles.billingSummaryCard}>
                  <span style={styles.billingSummaryAmount}>{billingRecords.filter(r => r.status === 'paid').length}</span>
                  <span style={styles.billingSummaryLabel}>Invoices Paid</span>
                </div>
                <div style={styles.billingSummaryCard}>
                  <span style={styles.billingSummaryAmount}>
                    {billingRecords.filter(r => r.status === 'paid').reduce((sum, r) => sum + (r.entryIds?.length || 0), 0)}
                  </span>
                  <span style={styles.billingSummaryLabel}>Days Billed</span>
                </div>
              </div>
              <h4 style={styles.billingHistoryTitle}>Payment History</h4>
              {billingLoading ? (
                <p style={styles.billingLoading}>Loading billing history...</p>
              ) : billingRecords.length === 0 ? (
                <div style={styles.billingEmpty}>
                  <p style={styles.billingEmptyIcon}>🧾</p>
                  <p style={styles.billingEmptyTitle}>No billing history yet</p>
                  <p style={styles.billingEmptyText}>Your weekly invoices will appear here after your first confirmed work week.</p>
                </div>
              ) : (
                <div style={styles.billingList}>
                  {billingRecords.map(record => (
                    <div key={record.id} style={styles.billingRow}>
                      <div style={styles.billingRowLeft}>
                        <p style={styles.billingRowTitle}>Week of {record.weekStart || '—'} — {record.weekEnding || '—'}</p>
                        <p style={styles.billingRowSub}>
                          {record.entryIds?.length || 0} day{(record.entryIds?.length || 0) !== 1 ? 's' : ''} confirmed
                          {record.paymentType === 'ach' ? ' • ACH Bank Transfer' : ' • Card'}
                          {record.paidAt?.toDate && ` • ${record.paidAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </p>
                      </div>
                      <div style={styles.billingRowRight}>
                        <span style={record.status === 'processing' ? styles.billingProcessingBadge : styles.billingPaidBadge}>
                          {record.status === 'processing' ? '⏳ Processing' : '✅ Paid'}
                        </span>
                        <span style={styles.billingAmount}>${(record.amount || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>ℹ️ How Billing Works</h3>
              <div style={styles.billingInfoList}>
                <div style={styles.billingInfoItem}>
                  <span style={styles.billingInfoStep}>1</span>
                  <div>
                    <p style={styles.billingInfoTitle}>Worker confirms hours each day</p>
                    <p style={styles.billingInfoText}>At the end of each work day, the worker logs hours and you confirm. If you don't respond within 24 hours, hours are auto-confirmed.</p>
                  </div>
                </div>
                <div style={styles.billingInfoItem}>
                  <span style={styles.billingInfoStep}>2</span>
                  <div>
                    <p style={styles.billingInfoTitle}>Hours queued for weekly billing</p>
                    <p style={styles.billingInfoText}>Verified hours go into the weekly billing queue. All days in the Sunday–Saturday work week are bundled into one charge.</p>
                  </div>
                </div>
                <div style={styles.billingInfoItem}>
                  <span style={styles.billingInfoStep}>3</span>
                  <div>
                    <p style={styles.billingInfoTitle}>Thursday billing cycle</p>
                    <p style={styles.billingInfoText}>Every Thursday at 8am ET your payment method is charged for all confirmed hours from the prior work week.</p>
                  </div>
                </div>
                <div style={styles.billingInfoItem}>
                  <span style={styles.billingInfoStep}>4</span>
                  <div>
                    <p style={styles.billingInfoTitle}>Workers paid simultaneously</p>
                    <p style={styles.billingInfoText}>
                      Once your charge is initiated, TradePower routes payment to the worker's bank.
                      {' '}<strong>ACH note:</strong> if paying by bank transfer, your ACH takes 3–5 days to settle —
                      workers are still paid Thursday via TradePower fronting the payment.
                    </p>
                  </div>
                </div>
              </div>
              <div style={styles.billingRateNote}>
                <p style={styles.billingRateNoteText}>
                  💰 <strong>Rate breakdown:</strong> You pay the contractor-facing rate shown on each worker's profile.
                  This includes the worker's take-home pay plus TradePower's 15% platform fee.
                  No hidden charges — the rate shown is what you pay per confirmed hour.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navbar: { backgroundColor: 'white', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer' },
  content: { maxWidth: '680px', margin: '0 auto', padding: '40px 24px' },
  title: { fontSize: '28px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  successBanner: { backgroundColor: '#d4edda', color: '#155724', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' },
  paymentFailedAlert: { backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '13px', color: '#721c24' },
  paymentFailedBtn: { padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },
  tabNav: { display: 'flex', gap: '8px', marginBottom: '24px', backgroundColor: 'white', borderRadius: '12px', padding: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  tab: { flex: 1, padding: '10px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  tabActive: { flex: 1, padding: '10px 16px', backgroundColor: '#E94560', color: 'white', border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  form: { display: 'flex', flexDirection: 'column', gap: '8px' },
  paymentsContent: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: { backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '8px' },
  sectionTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  hint: { color: '#999', fontSize: '13px', marginBottom: '16px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px' },
  input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '15px', boxSizing: 'border-box', marginBottom: '16px' },
  row: { display: 'flex', gap: '12px' },
  tagGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tag: { padding: '8px 16px', backgroundColor: 'white', color: '#666', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '14px' },
  tagActive: { padding: '8px 16px', backgroundColor: '#E94560', color: 'white', border: '1px solid #E94560', borderRadius: '20px', cursor: 'pointer', fontSize: '14px' },

  // SMS Toggle
  smsToggleBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #e0e0e0', gap: '12px' },
  smsToggleLeft: { flex: 1 },
  smsToggleTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  smsToggleDesc: { fontSize: '12px', color: '#666', lineHeight: '1.4' },
  smsToggleOn: { padding: '8px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },
  smsToggleOff: { padding: '8px 20px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },

  agreementsList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' },
  agreementItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px', backgroundColor: '#f8f9fa', borderRadius: '10px', cursor: 'pointer', border: '2px solid transparent' },
  agreementItemChecked: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px', backgroundColor: '#f0fff4', borderRadius: '10px', cursor: 'pointer', border: '2px solid #28a745' },
  agreementCheckbox: { flexShrink: 0, marginTop: '1px' },
  checkboxChecked: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', backgroundColor: '#28a745', borderRadius: '6px', color: 'white', fontSize: '13px', fontWeight: 'bold' },
  checkboxUnchecked: { display: 'block', width: '22px', height: '22px', border: '2px solid #ddd', borderRadius: '6px', backgroundColor: 'white' },
  agreementTextBlock: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  agreementText: { fontSize: '13px', color: '#333', lineHeight: '1.5' },
  requiredTag: { fontSize: '11px', color: '#155724', backgroundColor: '#d4edda', padding: '2px 8px', borderRadius: '6px', alignSelf: 'flex-start' },
  optionalTag: { fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', padding: '2px 8px', borderRadius: '6px', alignSelf: 'flex-start' },
  insuranceWarning: { backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#856404', marginBottom: '16px', lineHeight: '1.5' },
  agreementWarning: { backgroundColor: '#fff3cd', color: '#856404', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  disclaimer: { backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '14px', marginTop: '8px' },
  disclaimerTitle: { fontSize: '13px', fontWeight: 'bold', color: '#856404', marginBottom: '6px' },
  disclaimerText: { fontSize: '12px', color: '#856404', lineHeight: '1.6' },
  saveButton: { padding: '16px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginTop: '8px' },
  saveButtonDisabled: { padding: '16px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'not-allowed', marginTop: '8px' },
  paymentFailedBox: { backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '10px', padding: '14px', marginBottom: '16px' },
  paymentFailedTitle: { fontSize: '14px', fontWeight: 'bold', color: '#721c24', marginBottom: '4px' },
  paymentFailedText: { fontSize: '13px', color: '#721c24', lineHeight: '1.5' },
  methodToggleRow: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' },
  methodOption: { backgroundColor: '#f8f9fa', border: '2px solid #e0e0e0', borderRadius: '12px', padding: '16px' },
  methodOptionActive: { backgroundColor: '#f0fff4', border: '2px solid #28a745', borderRadius: '12px', padding: '16px' },
  methodOptionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  methodOptionIcon: { fontSize: '24px', flexShrink: 0 },
  methodOptionInfo: { flex: 1 },
  methodOptionTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px' },
  methodOptionSub: { fontSize: '12px', color: '#666' },
  activeMethodBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 },
  activeMethodBadgeACH: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 },
  methodOnFileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  methodOnFileLabel: { fontSize: '13px', color: '#555', fontWeight: 'bold' },
  updateMethodBtn: { padding: '7px 14px', backgroundColor: 'transparent', color: '#28a745', border: '1px solid #28a745', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 },
  addMethodBtn: { width: '100%', padding: '10px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  addMethodBtnACH: { width: '100%', padding: '10px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  achRecommendedNote: { fontSize: '12px', color: '#2c5282', textAlign: 'center', marginTop: '6px', fontStyle: 'italic' },
  activeMethodSummary: { display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '10px', padding: '14px 16px', marginTop: '8px' },
  activeMethodSummaryIcon: { fontSize: '24px', flexShrink: 0 },
  activeMethodSummaryLabel: { fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' },
  activeMethodSummaryValue: { fontSize: '15px', fontWeight: 'bold' },
  cardFormBox: { backgroundColor: '#f8f9fa', border: '2px solid #E94560', borderRadius: '10px', padding: '20px', marginTop: '8px' },
  cardFormTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  cardFormSubtitle: { fontSize: '13px', color: '#555', lineHeight: '1.5', marginBottom: '14px' },
  cardFormInfoBox: { display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#e8f4fd', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' },
  billingSummaryRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' },
  billingSummaryCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', gap: '4px' },
  billingSummaryAmount: { fontSize: '24px', fontWeight: 'bold', color: '#E94560' },
  billingSummaryLabel: { fontSize: '12px', color: '#999' },
  billingHistoryTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' },
  billingLoading: { fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' },
  billingEmpty: { textAlign: 'center', padding: '32px' },
  billingEmptyIcon: { fontSize: '36px', marginBottom: '10px' },
  billingEmptyTitle: { fontSize: '15px', color: '#1a1a2e', fontWeight: 'bold', marginBottom: '6px' },
  billingEmptyText: { fontSize: '13px', color: '#999' },
  billingList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  billingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '14px 16px', flexWrap: 'wrap', gap: '8px' },
  billingRowLeft: { flex: 1 },
  billingRowTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px' },
  billingRowSub: { fontSize: '12px', color: '#999' },
  billingRowRight: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  billingPaidBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  billingProcessingBadge: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  billingAmount: { fontSize: '16px', fontWeight: 'bold', color: '#E94560' },
  billingInfoList: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' },
  billingInfoItem: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  billingInfoStep: { width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#E94560', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },
  billingInfoTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  billingInfoText: { fontSize: '13px', color: '#666', lineHeight: '1.5' },
  billingRateNote: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '14px' },
  billingRateNoteText: { fontSize: '13px', color: '#276749', lineHeight: '1.6' },
  deleteSection: { marginTop: '8px' },
  deleteDivider: { borderTop: '1px solid #f0f0f0', marginBottom: '24px', marginTop: '16px' },
  deleteTitle: { fontSize: '15px', fontWeight: 'bold', color: '#dc3545', marginBottom: '8px' },
  deleteText: { fontSize: '13px', color: '#666', lineHeight: '1.5', marginBottom: '14px' },
  deleteBtn: { padding: '10px 20px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  deleteConfirmBox: { backgroundColor: '#fff5f5', border: '2px solid #dc3545', borderRadius: '10px', padding: '20px' },
  deleteConfirmTitle: { fontSize: '15px', fontWeight: 'bold', color: '#dc3545', marginBottom: '12px' },
  deleteConfirmList: { marginBottom: '16px' },
  deleteConfirmItem: { fontSize: '13px', color: '#721c24', marginBottom: '4px' },
  deleteConfirmLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '8px' },
  deleteConfirmInput: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #dc3545', fontSize: '15px', boxSizing: 'border-box', marginBottom: '12px', fontWeight: 'bold', letterSpacing: '2px' },
  deleteError: { fontSize: '13px', color: '#dc3545', marginBottom: '12px', fontWeight: 'bold' },
  deleteConfirmButtons: { display: 'flex', gap: '10px' },
  deleteCancelBtn: { flex: 1, padding: '11px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  deleteConfirmBtn: { flex: 2, padding: '11px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  deleteConfirmBtnDisabled: { flex: 2, padding: '11px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
};

export default ContractorProfile;